/** Developer-only audio pipeline panel. Hidden unless
 *  `audio-debug.enabled` is true; toggle with Cmd/Ctrl+Shift+D or
 *  GlobalState.set('audio-debug.enabled', true). */
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import * as Sentry from '@sentry/electron/renderer'
import { createSnapshotConsumer, GlobalEvents } from '@highlight/ipc'
import { useGlobalState, useSnapshotConsumer } from '@highlight/ipc/react'
import {
  AUDIO_DEBUG_DUMP_WAVS_EVENT,
  AUDIO_DEBUG_ENABLED_KEY,
  AUDIO_DEBUG_REVEAL_EVENT,
  AUDIO_DEBUG_TOPIC,
  EMPTY_AUDIO_DEBUG_SNAPSHOT,
  type AudioDebugChannelState,
  type AudioDebugDumpResult,
  type AudioDebugDumpedFile,
  type AudioDebugEvent,
  type AudioDebugRecallState,
  type AudioDebugRouting,
  type AudioDebugSnapshot,
} from '@shared/audio-debug'
import { snapshotAndPublishAudioRouting } from '@renderer/services/audio-transcription/audio-debug'
import { MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE, type MeetingMicGate } from '@shared/types/meeting-mic-gate'
import { useMeetingParticipants } from '@renderer/hooks/audio/use-meeting-participants'
import type { MeetingDetectionDebugState } from '@shared/types/meeting-detection-debug'
import { DebugOverlay } from '@renderer/components/debug-overlay'

/** Cmd/Ctrl+Shift+D avoids the browser's Cmd/Ctrl+D shortcut. */
const isHotkey = (e: KeyboardEvent): boolean =>
  e.shiftKey && (e.key === 'D' || e.key === 'd') && (e.metaKey || e.ctrlKey)

/** Same 5s cadence as the audio runtime's channel diagnostics tick. */
const ROUTING_REFRESH_MS = 5000

const audioDebugConsumer = createSnapshotConsumer<AudioDebugSnapshot>({
  topic: AUDIO_DEBUG_TOPIC,
  initialSnapshot: EMPTY_AUDIO_DEBUG_SNAPSHOT,
  onError: (error) => {
    Sentry.captureException(error, { tags: { module: 'audio-debug-overlay' } })
  },
})

export const AudioDebugOverlay = memo(function AudioDebugOverlay() {
  const [enabled, setEnabled] = useGlobalState<boolean>(AUDIO_DEBUG_ENABLED_KEY, false)

  // Stable listener for the overlay window lifetime.
  const toggle = useCallback(() => {
    setEnabled((prev) => !(prev === true))
  }, [setEnabled])
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isHotkey(e)) {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [toggle])

  if (!enabled) return null
  return <DebugPanel onClose={toggle} />
})
AudioDebugOverlay.displayName = 'AudioDebugOverlay'

const DebugPanel = memo(function DebugPanel({ onClose }: { onClose: () => void }) {
  // The audio-debug data streams over the broadcast broker while this panel
  // is mounted (with it closed, the main store has zero consumers and
  // nothing crosses IPC). The shared consumer subscribes before pulling the
  // current snapshot and uses revisions to reject stale pull responses.
  const snapshot = useSnapshotConsumer(audioDebugConsumer)
  const { routing, mic: micState, sys: sysState, recall: recallState, meetingDetection, events } = snapshot

  const [meetingMicGate] = useGlobalState<MeetingMicGate>(MEETING_MIC_GATE_KEY, NEUTRAL_MEETING_MIC_GATE)
  const participants = useMeetingParticipants()

  // This panel owns the periodic routing refresh: audio routing is
  // machine-global, and this renderer is the only one guaranteed alive
  // whenever the panel is open (the audio-runtime window doesn't exist
  // under the Recall runtime). Capture sources still publish event-based
  // snapshots at acquire time.
  useEffect(() => {
    void snapshotAndPublishAudioRouting()
    const timer = setInterval(() => void snapshotAndPublishAudioRouting(), ROUTING_REFRESH_MS)
    return () => clearInterval(timer)
  }, [])

  // Only render the capture cards that can exist under the active runtime:
  // recall recordings have no electron sys/mic channels, electron recordings
  // have no recall surface, and native has neither instrumented. The
  // snapshot's runtimeType is non-null exactly while a runtime is live
  // (AudioNoteStateManager republishes on every state change). When it's
  // null, fall back to whichever pipeline actually published state so a
  // just-finished session stays inspectable instead of vanishing.
  const runtimeType = meetingDetection?.audioNote?.runtimeType ?? null
  const showChannelCards = runtimeType ? runtimeType === 'electron' : Boolean(sysState || micState)
  const showRecallCard = runtimeType ? runtimeType === 'recall' : recallState !== null

  return (
    <DebugOverlay
      label="audio debug"
      onClose={onClose}
      placementClassName="left-4 top-4"
      className="max-h-[80vh] w-[360px]"
      selectable
    >
      <div className="flex min-h-0 flex-col gap-2 overflow-y-auto p-2">
        <RoutingCard routing={routing} />
        <MeetingCard micGate={meetingMicGate} participants={participants} />
        <MeetingDetectionCard debug={meetingDetection} />
        {showChannelCards && (
          <>
            <ChannelCard label="sys" state={sysState} />
            <ChannelCard label="mic" state={micState} />
            <WavDumpCard />
          </>
        )}
        {showRecallCard && <RecallCard state={recallState} />}
        <EventsCard events={events} />
      </div>
    </DebugOverlay>
  )
})
DebugPanel.displayName = 'DebugPanel'

const RoutingCard = memo(function RoutingCard({ routing }: { routing: AudioDebugRouting | null }) {
  if (!routing) {
    return (
      <Section title="Routing">
        <Empty>no routing snapshot yet (acquire a capture)</Empty>
      </Section>
    )
  }
  return (
    <Section title="Routing">
      <Row label="output" value={routing.defaultOutputLabel} />
      <Row label="input" value={routing.defaultInputLabel} />
      {routing.bluetoothOutputs.length > 0 && (
        <Row label="bt out" value={routing.bluetoothOutputs.map((d) => d.label).join(', ')} />
      )}
      {routing.bluetoothInputs.length > 0 && (
        <Row label="bt in" value={routing.bluetoothInputs.map((d) => d.label).join(', ')} />
      )}
      <div className="mt-1 flex items-center gap-2">
        <span className="text-foreground-secondary">HFP risk</span>
        <span
          className={twMerge(
            'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider',
            routing.hfpTriggerRisk
              ? 'bg-danger-subtle-hover text-danger-subtle'
              : 'bg-success-subtle-hover text-success-subtle',
          )}
        >
          {routing.hfpTriggerRisk ? 'HIGH' : 'low'}
        </span>
      </div>
      <Row label="taken" value={fmtRelMs(routing.takenAtMs)} muted />
    </Section>
  )
})
RoutingCard.displayName = 'RoutingCard'

const MeetingCard = memo(function MeetingCard({
  micGate,
  participants,
}: {
  micGate: MeetingMicGate
  participants: Record<number, TrackedParticipant>
}) {
  const participantCount = Object.keys(participants).length
  return (
    <Section title="Meeting">
      <Row label="participants" value={String(participantCount)} muted={participantCount === 0} />
      <Row
        label="self mute"
        value={micGate.selfMute === null ? 'unknown' : micGate.selfMute ? 'muted' : 'unmuted'}
        warn={micGate.selfMute === true}
        muted={micGate.selfMute === null}
      />
      <Row label="merged" value={micGate.mergedAudio ? 'yes' : 'no'} muted={!micGate.mergedAudio} />
      <Row
        label="capture while muted"
        value={micGate.captureWhileMuted ? 'yes' : 'no'}
        muted={!micGate.captureWhileMuted}
      />
      <Row
        label="mic gate"
        value={micGate.shouldDropMic ? 'dropping mic' : 'capturing mic'}
        warn={micGate.shouldDropMic}
      />
    </Section>
  )
})
MeetingCard.displayName = 'MeetingCard'

const MeetingDetectionCard = memo(function MeetingDetectionCard({
  debug,
}: {
  debug: MeetingDetectionDebugState | null
}) {
  if (!debug) {
    return (
      <Section title="Detection">
        <Empty>no detection snapshot yet</Empty>
      </Section>
    )
  }

  const current = debug.audioNote?.current
  const pending = debug.audioNote?.pending
  const detected = debug.audioNote?.detected

  return (
    <Section title="Detection">
      <Row label="source" value={debug.source} />
      <Row label="status" value={debug.audioNote?.status ?? 'unknown'} muted={!debug.audioNote?.status} />
      <Row label="runtime" value={debug.audioNote?.runtimeType ?? 'none'} muted={!debug.audioNote?.runtimeType} />

      <Row label="current win" value={current?.windowId ?? 'none'} muted={!current?.windowId} />
      <Row label="current url" value={current?.url ?? 'none'} muted={!current?.url} />
      <Row label="pending win" value={pending?.windowId ?? 'none'} muted={!pending?.windowId} />
      <Row label="pending url" value={pending?.url ?? 'none'} muted={!pending?.url} />
      <Row label="detected win" value={detected?.windowId ?? 'none'} muted={!detected?.windowId} />
      <Row label="detected url" value={detected?.url ?? 'none'} muted={!detected?.url} />

      <Row
        label="recall win"
        value={debug.recall?.lastUpdatedWindowId ?? 'none'}
        muted={!debug.recall?.lastUpdatedWindowId}
      />
      <Row label="recall url" value={debug.recall?.lastUpdatedUrl ?? 'none'} muted={!debug.recall?.lastUpdatedUrl} />

      <Row
        label="dismissed"
        value={`id ${debug.detector?.dismissedIdentityCount ?? 0} / sig ${debug.detector?.dismissedSignalCount ?? 0}`}
        warn={(debug.detector?.dismissedIdentityCount ?? 0) > 0 || (debug.detector?.dismissedSignalCount ?? 0) > 0}
      />
      <Row
        label="ended"
        value={debug.detector?.recentlyEndedMeetingUrl ?? 'none'}
        muted={!debug.detector?.recentlyEndedMeetingUrl}
      />
      <Row label="updated" value={fmtRelMs(debug.updatedAtMs)} muted />
    </Section>
  )
})
MeetingDetectionCard.displayName = 'MeetingDetectionCard'

const ChannelCard = memo(function ChannelCard({
  label,
  state,
}: {
  label: 'mic' | 'sys'
  state: AudioDebugChannelState | null
}) {
  if (!state) {
    return (
      <Section title={label.toUpperCase()}>
        <Empty>channel inactive</Empty>
      </Section>
    )
  }
  // Hide measured-vs-claimed until a passive rate measurement exists.
  const showMeasured = state.measuredSampleRate > 0
  const measuredOk =
    !showMeasured || Math.abs(state.measuredSampleRate / Math.max(1, state.claimedSampleRate) - 1) < 0.05
  return (
    <Section
      title={label.toUpperCase()}
      headerRight={
        <span
          className={twMerge(
            'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
            state.phase === 'acquired'
              ? 'bg-success-subtle-hover text-success-subtle'
              : state.phase === 'ended'
                ? 'bg-danger-subtle-hover text-danger-subtle'
                : 'bg-element-hover text-foreground-secondary',
          )}
        >
          {state.phase}
        </span>
      }
    >
      <Row label="claimed" value={`${state.claimedSampleRate} Hz`} />
      {showMeasured && <Row label="measured" value={`${state.measuredSampleRate.toFixed(0)} Hz`} warn={!measuredOk} />}
      <Row label="target" value={`${state.targetSampleRate} Hz`} muted />
      <Row label="frames/s" value={state.framesPerSecond.toFixed(1)} />
      <Row label="rms" value={state.rmsMean.toFixed(4)} muted={state.rmsMean < 0.001} />
      <Row label="peak" value={state.peakAbs.toFixed(4)} muted={state.peakAbs < 0.001} />
      <Row
        label="ws sent"
        value={`${state.bytesSentKbps.toFixed(1)} KB/s · ${(state.bytesSentTotal / 1024).toFixed(0)} KB total`}
        muted={state.bytesSentKbps < 0.1}
      />
      <Row
        label="buffered"
        value={`${(state.bufferedBytes / 1024).toFixed(1)} KB`}
        warn={state.bufferedBytes > 64_000}
      />
      <Row
        label="transcripts"
        value={`${state.transcriptsTotal} (${state.wsMessagesTotal} ws msgs)`}
        warn={state.bytesSentTotal > 100_000 && state.transcriptsTotal === 0}
        muted={state.bytesSentTotal === 0}
      />
      <Row label="ws" value={`${state.wsReadyState} ${state.serverReady ? '(ready)' : '(handshaking)'}`} />
      <Row label="track" value={state.liveTrack ? 'live' : 'ended'} warn={!state.liveTrack} />
      <Row label="frames" value={String(state.totalFrames)} muted />
      <Row label="updated" value={fmtRelMs(state.takenAtMs)} muted />
    </Section>
  )
})
ChannelCard.displayName = 'ChannelCard'

/** Recall-runtime card. Capture/WS/upload live inside the Recall SDK, so
 *  instead of byte-level channel stats this shows the observable realtime
 *  surface: mixed-audio delivery cadence, transcript-event counts, and
 *  SDK lifecycle. Published from the main process (`recall-audio-debug.ts`). */
const RecallCard = memo(function RecallCard({ state }: { state: AudioDebugRecallState | null }) {
  if (!state) {
    return (
      <Section title="Recall">
        <Empty>runtime inactive</Empty>
      </Section>
    )
  }
  const audioStalled = state.sdkState === 'recording' && state.audioEventsPerSecond === 0
  return (
    <Section
      title="Recall"
      headerRight={
        <span
          className={twMerge(
            'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
            state.sdkState === 'recording'
              ? 'bg-success-subtle-hover text-success-subtle'
              : state.sdkState === 'paused'
                ? 'bg-warning-subtle-hover text-warning-subtle'
                : 'bg-element-hover text-foreground-secondary',
          )}
        >
          {state.sdkState}
        </span>
      }
    >
      <Row label="window" value={state.windowId ?? 'none'} muted={!state.windowId} />
      <Row label="recording id" value={state.recallRecordingId ?? 'none'} muted={!state.recallRecordingId} />
      <Row label="session id" value={state.audioSessionId ?? 'none'} muted={!state.audioSessionId} />
      <Row label="audio ev/s" value={state.audioEventsPerSecond.toFixed(1)} warn={audioStalled} />
      <Row label="audio" value={`${state.audioKbps.toFixed(1)} KB/s`} muted={state.audioKbps < 0.1} />
      <Row
        label="level"
        value={`${state.lastAudioLevel}/5${state.lastAudioAtMs > 0 ? ` · ${fmtRelMs(state.lastAudioAtMs)}` : ''}`}
        muted={state.lastAudioLevel === 0}
      />
      <Row
        label="transcripts"
        value={`${state.transcriptEventsTotal} (${state.partialEventsTotal} partial · ${state.providerEventsTotal} provider)`}
        warn={state.sdkState === 'recording' && state.transcriptEventsTotal === 0 && state.providerEventsTotal === 0}
      />
      <Row
        label="last transcript"
        value={state.lastTranscriptAtMs > 0 ? fmtRelMs(state.lastTranscriptAtMs) : 'never'}
        muted={state.lastTranscriptAtMs === 0}
      />
      <Row
        label="network"
        value={state.networkStatus ?? 'unknown'}
        warn={state.networkStatus === 'disconnected'}
        muted={state.networkStatus === null}
      />
      <Row label="updated" value={fmtRelMs(state.takenAtMs)} muted />
    </Section>
  )
})
RecallCard.displayName = 'RecallCard'

/** Dump the per-channel debug ring buffers (the exact PCM bytes shipped
 *  to Deepgram) to .wav files on disk, and play them back inline — so
 *  pipeline quality can be verified by ear instead of trusted blindly.
 *  Playback streams the on-disk files through the `audio-debug://`
 *  protocol; no WAV bytes cross into this renderer. */
const WavDumpCard = memo(function WavDumpCard() {
  const [dumping, setDumping] = useState(false)
  const [reason, setReason] = useState<string | null>(null)
  const [dirPath, setDirPath] = useState<string | null>(null)
  const [files, setFiles] = useState<Array<AudioDebugDumpedFile>>([])

  const dump = useCallback(async () => {
    setDumping(true)
    try {
      const result = await GlobalEvents.invoke<AudioDebugDumpResult>(AUDIO_DEBUG_DUMP_WAVS_EVENT)
      if (!result.ok) {
        setReason(result.reason)
        setDirPath(null)
        setFiles([])
        return
      }
      setReason(null)
      setDirPath(result.dirPath)
      setFiles(result.files)
    } catch (err) {
      console.warn('[audio-debug-overlay] WAV dump failed:', err)
      setReason(err instanceof Error ? err.message : String(err))
      setDirPath(null)
      setFiles([])
    } finally {
      setDumping(false)
    }
  }, [])

  const reveal = useCallback((filePath: string) => {
    GlobalEvents.invoke(AUDIO_DEBUG_REVEAL_EVENT, { filePath }).catch((err) => {
      console.warn('[audio-debug-overlay] reveal failed:', err)
    })
  }, [])

  return (
    <Section
      title="WAV Dump"
      headerRight={
        <button
          type="button"
          onClick={() => void dump()}
          disabled={dumping}
          className="text-foreground-secondary hover:text-foreground rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
        >
          {dumping ? 'dumping…' : 'dump mic + sys'}
        </button>
      }
    >
      {reason && <div className="text-warning-subtle break-words">{reason}</div>}
      {!reason && files.length === 0 && (
        <Empty>
          records while this overlay is enabled — saves the exact PCM sent to Deepgram plus the pre-resample raw mic
          audio (last ~30s each) as .wav
        </Empty>
      )}
      {dirPath && <Row label="folder" value={dirPath} muted />}
      {files.map((f) => (
        <div key={f.filePath} className="-mx-1 flex flex-col gap-1 border-t px-1 py-1 first:border-t-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-foreground font-semibold" title={f.description}>
              {f.channel} · {f.name}
            </span>
            <span className="text-foreground-secondary shrink-0">
              {f.durationSec.toFixed(1)}s @ {(f.sampleRate / 1000).toFixed(1)}kHz
            </span>
          </div>
          {/* Native controls give play/pause/seek for free at this panel width. */}
          <audio controls preload="metadata" src={f.url} title={f.description} className="h-8 w-full" />
          <button
            type="button"
            onClick={() => reveal(f.filePath)}
            className="text-foreground-secondary hover:text-foreground self-start underline decoration-dotted underline-offset-2 transition-colors"
            title={f.filePath}
          >
            reveal in Finder
          </button>
        </div>
      ))}
    </Section>
  )
})
WavDumpCard.displayName = 'WavDumpCard'

const EventsCard = memo(function EventsCard({ events }: { events: Array<AudioDebugEvent> }) {
  // Newest first for the triage path.
  const sorted = useMemo(() => [...events].reverse(), [events])
  return (
    <Section title={`Events (${events.length})`}>
      {sorted.length === 0 && <Empty>no events yet</Empty>}
      {sorted.map((e, i) => (
        <div
          key={`${e.atMs}-${i}`}
          className={twMerge('-mx-1 flex items-start gap-2 border-t px-1 py-1 first:border-t-0')}
        >
          <span
            className={twMerge(
              'mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full',
              e.severity === 'error' ? 'bg-danger' : e.severity === 'warn' ? 'bg-warning' : 'bg-foreground-placeholder',
            )}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-foreground-secondary text-[10px]">
                {e.channel ?? 'svc'} · {fmtRelMs(e.atMs)}
              </span>
            </div>
            <div className="text-foreground break-words">{e.message}</div>
          </div>
        </div>
      ))}
    </Section>
  )
})
EventsCard.displayName = 'EventsCard'

const Section = memo(function Section({
  title,
  headerRight,
  children,
}: {
  title: string
  headerRight?: ReactNode
  children: ReactNode
}) {
  const [expanded, setExpanded] = useState(true)
  return (
    <div className="rounded border p-2">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-foreground-secondary text-[10px] font-bold uppercase tracking-wider">{title}</span>
        <div className="flex items-center gap-2">
          {headerRight}
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="text-foreground-secondary hover:text-foreground rounded p-0.5 transition-colors"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
          >
            {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </button>
        </div>
      </div>
      {expanded && <div className="flex flex-col gap-0.5">{children}</div>}
    </div>
  )
})
Section.displayName = 'Section'

const Row = memo(function Row({
  label,
  value,
  warn = false,
  muted = false,
}: {
  label: string
  value: string
  warn?: boolean
  muted?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-foreground-secondary shrink-0">{label}</span>
      <span
        className={twMerge(
          'truncate text-right',
          warn ? 'text-warning-subtle font-bold' : muted ? 'text-foreground-placeholder' : 'text-foreground',
        )}
        title={value}
      >
        {value}
      </span>
    </div>
  )
})
Row.displayName = 'Row'

const Empty = memo(function Empty({ children }: { children: ReactNode }) {
  return <div className="text-foreground-placeholder italic">{children}</div>
})
Empty.displayName = 'Empty'

function fmtRelMs(ms: number): string {
  const dt = Date.now() - ms
  if (dt < 1000) return `${dt}ms ago`
  if (dt < 60_000) return `${Math.floor(dt / 1000)}s ago`
  if (dt < 3_600_000) return `${Math.floor(dt / 60_000)}m ago`
  return `${Math.floor(dt / 3_600_000)}h ago`
}
